import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormsModule } from '@angular/forms';

@Component({
  selector: 'app-radio-button',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './radio-button.component.html',
  styleUrls: ['./radio-button.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioButtonComponent),
      multi: true
    }
  ]
})
export class RadioButtonComponent implements ControlValueAccessor, OnInit {
  @Input() value!: string | boolean | number;
  @Input() name!: string;
  @Input() label!: string;
  @Input() id!: string; // Made required for accessibility
  @Input() disabled: boolean = false;
  @Input() customClasses: string = '';
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>(); // For standalone and change events

  private _value: any;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  get valueModel(): any {
    return this._value;
  }

  set valueModel(value: any) {
    if (value !== this._value) {
      this._value = value;
      this.onChange(value);
      this.valueChange.emit(value);
    }
  }

  ngOnInit() {
    if (this.value === undefined || !this.name || !this.label || !this.id) {
      console.log('RadioButtonComponent: value, name, label, and id are required');
    }
  }

  writeValue(value: any): void {
    this._value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onLabelClick(event: Event): void {
    // Prevent label click from interfering if input is disabled
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    const newValue = this.parseValue(String(this.value));
    this.valueModel = newValue;
    this.valueChange.emit(newValue);
    this.onTouched();
  }

  onRadioChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = this.parseValue(input.value);
    this.valueModel = newValue;
    this.valueChange.emit(newValue);
    this.onTouched();
  }

  // Parse value to handle different types (e.g., 'true' -> true)
  private parseValue(value: string): any {
    if (value === 'true') return true;
    if (value === 'false') return false;
    if (!isNaN(Number(value))) return Number(value);
    return value;
  }
}